﻿/**
 * Cart page: accessory bundle upgrade — swap included bundle lines for upgraded product.
 * Depends on section markup + #accessory-bundle-upsell-popup-config (templateContext: cart).
 */
(function () {
  'use strict';

  function normalizeBedSizeLabel(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[\u2013\u2014]/g, '-')
      .replace(/[\u2013\u2014]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function compactBedSizeKey(value) {
    return normalizeBedSizeLabel(value).replace(/\s+/g, '');
  }

  function bedSizeLabelsEqual(a, b) {
    const na = normalizeBedSizeLabel(a);
    const nb = normalizeBedSizeLabel(b);
    if (!na || !nb) return false;
    if (na === nb) return true;
    return compactBedSizeKey(a) === compactBedSizeKey(b);
  }

  function getSizeLikeOptionIndexes(product) {
    const indexes = [];
    if (product && product.options && product.options.length) {
      product.options.forEach(function (name, idx) {
        const n = String(name || '').toLowerCase();
        if (n.includes('size') || n.includes('dimension') || n.includes('width') || n.includes('mattress')) {
          indexes.push(idx);
        }
      });
    }
    return indexes;
  }

  function variantOptionValuesAll(variant) {
    const out = [];
    if (variant && variant.options && Array.isArray(variant.options)) {
      variant.options.forEach(function (o) {
        if (o != null && String(o).trim() !== '') out.push(String(o).trim());
      });
    }
    if (out.length) return out;
    if (variant.option1) out.push(String(variant.option1).trim());
    if (variant.option2) out.push(String(variant.option2).trim());
    if (variant.option3) out.push(String(variant.option3).trim());
    return out;
  }

  function isVariantSellableForUpsell(v) {
    if (!v) return false;
    if (v.available === true) return true;
    if (v.inventory_policy === 'continue') return true;
    if (v.inventory_management == null && v.available !== false) return true;
    const qty = v.inventory_quantity;
    if (qty != null && parseInt(qty, 10) > 0) return true;
    return false;
  }

  function firstSellableOrFirstVariant(product) {
    if (!product || !product.variants || !product.variants.length) return null;
    const avail = product.variants.find(isVariantSellableForUpsell);
    return avail || product.variants[0];
  }

  function getVariantBySize(product, sizeValue) {
    if (!product || !product.variants || !product.variants.length) return null;
    const wantRaw = (sizeValue || '').toString().trim();
    if (!wantRaw) return firstSellableOrFirstVariant(product);

    const preferredIdxs = getSizeLikeOptionIndexes(product);
    const optLen = (product.options && product.options.length) || 0;
    const tryIndexes = preferredIdxs.length
      ? preferredIdxs
      : [0, 1, 2].filter(function (i) {
          return i < optLen;
        });

    function valueAtVariantIndex(variant, ix) {
      if (variant.options && variant.options[ix] != null) return String(variant.options[ix]).trim();
      if (ix === 0) return variant.option1 ? String(variant.option1).trim() : '';
      if (ix === 1) return variant.option2 ? String(variant.option2).trim() : '';
      if (ix === 2) return variant.option3 ? String(variant.option3).trim() : '';
      return '';
    }

    function variantMatchesSize(variant) {
      if (tryIndexes.length) {
        for (let ti = 0; ti < tryIndexes.length; ti++) {
          const val = valueAtVariantIndex(variant, tryIndexes[ti]);
          if (val && bedSizeLabelsEqual(val, wantRaw)) return true;
        }
      }
      const allVals = variantOptionValuesAll(variant);
      for (let ai = 0; ai < allVals.length; ai++) {
        if (bedSizeLabelsEqual(allVals[ai], wantRaw)) return true;
      }
      return false;
    }

    const matches = product.variants.filter(variantMatchesSize);
    if (!matches.length) return firstSellableOrFirstVariant(product);

    const sellableMatch = matches.find(isVariantSellableForUpsell);
    if (sellableMatch) return sellableMatch;
    return product.variants[0] || matches[0];
  }

  function formatMoneyFromCents(cents) {
    if (cents == null || isNaN(cents)) return '$0';
    const num = typeof cents === 'string' ? parseFloat(cents.replace(/[^0-9.-]/g, '')) : Number(cents);
    const dollars = Math.round(num) / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Math.max(0, dollars));
  }

  function lineProps(line) {
    return line.properties || {};
  }

  function isMattressLine(line) {
    const p = lineProps(line);
    return p._mattress_size != null && String(p._mattress_size).trim() !== '';
  }

  function isIncludedAccessoryBundleLine(line) {
    const p = lineProps(line);
    if (!p._bundle_group_id || !p['Linked to']) return false;
    if (p['Bundle'] === 'Upgraded accessory bundle') return false;
    return p['Included Items'] != null && String(p['Included Items']).trim() !== '';
  }

  function groupHasUpgradedBundleLine(lines) {
    return lines.some(function (line) {
      return lineProps(line)['Bundle'] === 'Upgraded accessory bundle';
    });
  }

  function foundationTriggerLineMatches(triggerLine, foundationType) {
    const trigger = String(triggerLine || '')
      .trim()
      .toLowerCase();
    if (!trigger) return false;
    const normalized = String(foundationType || '')
      .trim()
      .toLowerCase();
    return normalized === trigger || normalized.includes(trigger) || trigger.includes(normalized);
  }

  function foundationMatchesCartLine(config, foundationTypeFromCart) {
    if (!config || config.foundationTriggerLines == null) return true;
    const lines = String(config.foundationTriggerLines)
      .split(/\r?\n/)
      .map(function (s) {
        return s.trim();
      })
      .filter(Boolean);
    if (!lines.length) return true;
    const ft = (foundationTypeFromCart || '').toString().trim();
    if (!ft) return true;
    return lines.some(function (line) {
      return foundationTriggerLineMatches(line, ft);
    });
  }

  function groupCartLinesByBundleId(cart) {
    const by = {};
    (cart.items || []).forEach(function (line) {
      const g = lineProps(line)._bundle_group_id;
      if (!g) return;
      if (!by[g]) by[g] = [];
      by[g].push(line);
    });
    return by;
  }

  function findFirstEligibleOffer(cart, config) {
    const groups = groupCartLinesByBundleId(cart);
    const ids = Object.keys(groups);
    for (let i = 0; i < ids.length; i++) {
      const groupId = ids[i];
      const items = groups[groupId];
      const mattress = items.find(isMattressLine);
      if (!mattress) continue;
      const mp = lineProps(mattress);
      if (mp._accessory_bundle_included !== '1') continue;
      if (mp._accessory_upgraded === '1') continue;
      if (groupHasUpgradedBundleLine(items)) continue;
      const accLines = items.filter(isIncludedAccessoryBundleLine);
      if (!accLines.length) continue;
      const ft = (mp._foundation_type || '').toString().trim();
      if (!foundationMatchesCartLine(config, ft)) continue;
      return { groupId: groupId, mattress: mattress, accLines: accLines };
    }
    return null;
  }

  function cartDeclineStorageKey(groupId) {
    return 'scAbbCartDecline:' + String(groupId || '');
  }

  function readDecline(groupId) {
    if (typeof sessionStorage === 'undefined') return null;
    try {
      return sessionStorage.getItem(cartDeclineStorageKey(groupId));
    } catch (e) {
      return null;
    }
  }

  function writeDecline(groupId) {
    if (typeof sessionStorage === 'undefined') return;
    try {
      sessionStorage.setItem(cartDeclineStorageKey(groupId), '1');
    } catch (e) {}
  }

  async function fetchCart() {
    const res = await fetch('/cart.js');
    if (!res.ok) throw new Error('Cart unavailable');
    return res.json();
  }

  async function changeLineQuantity(key, qty) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty }),
    });
    if (!res.ok) {
      const err = await res.json().catch(function () {
        return {};
      });
      throw new Error(err.description || err.message || 'Could not update cart');
    }
    return res.json();
  }

  async function addUpgradeLine(variantId, properties) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: parseInt(variantId, 10), quantity: 1, properties: properties }],
      }),
    });
    if (!res.ok) {
      const err = await res.json().catch(function () {
        return {};
      });
      throw new Error(err.description || err.message || 'Could not add upgrade');
    }
    return res.json();
  }

  function byId(id) {
    return document.getElementById(id);
  }

  async function runCartAccessoryUpgrade() {
    const overlay = byId('accessory-bundle-upsell-popup-overlay');
    const configEl = byId('accessory-bundle-upsell-popup-config');
    if (!overlay || !configEl || overlay.getAttribute('data-abb-context') !== 'cart') return;

    let config;
    try {
      config = JSON.parse(configEl.textContent);
    } catch (e) {
      return;
    }
    if (!config || config.templateContext !== 'cart' || !config.upgradeProductHandle) return;

    const cart = await fetchCart();
    const offer = findFirstEligibleOffer(cart, config);
    if (!offer) return;

    if (config.rememberChoice !== false && readDecline(offer.groupId)) return;

    const sizeValue = String(lineProps(offer.mattress)._mattress_size || '').trim();
    const mainTitle = offer.mattress.product_title || offer.mattress.title || 'Mattress';
    const linkedName = mainTitle;

    let includedCents = 0;
    for (let i = 0; i < offer.accLines.length; i++) {
      const line = offer.accLines[i];
      let handle = line.handle ? String(line.handle) : '';
      if (!handle && line.url) {
        const m = String(line.url).match(/\/products\/([^?]+)/);
        if (m) handle = m[1];
      }
      if (!handle) continue;
      try {
        const pr = await fetch('/products/' + encodeURIComponent(handle) + '.js');
        if (!pr.ok) continue;
        const prod = await pr.json();
        const v =
          prod.variants &&
          prod.variants.find(function (x) {
            return String(x.id) === String(line.variant_id);
          });
        if (v && v.price != null) includedCents += parseInt(v.price, 10) || 0;
      } catch (e) {}
    }

    const upRes = await fetch('/products/' + encodeURIComponent(String(config.upgradeProductHandle)) + '.js');
    if (!upRes.ok) return;
    const upgradeProduct = await upRes.json();
    const upgradeVariant = getVariantBySize(upgradeProduct, sizeValue) || (upgradeProduct.variants && upgradeProduct.variants[0]);
    if (!upgradeVariant) return;

    const upgradeCents = parseInt(upgradeVariant.price, 10) || 0;
    const diffCents = Math.max(0, upgradeCents - includedCents);
    const priceFormatted = formatMoneyFromCents(diffCents);

    const diffMain = byId('accessory-bundle-upsell-popup-diff');
    const compareTop = byId('accessory-bundle-upsell-popup-compare-at');
    if (compareTop) {
      const compareRaw = config.compareAtDifference ? String(config.compareAtDifference).trim() : '';
      compareTop.textContent = compareRaw ? compareRaw : '';
    }
    if (diffMain && config.subheadTemplate) {
      diffMain.textContent = String(config.subheadTemplate).replace(/\{\{price\}\}|\$X/g, priceFormatted);
    }

    const priceHidden = byId('accessory-bundle-upsell-popup-price');
    if (priceHidden) priceHidden.textContent = priceFormatted;

    const curLabel = byId('abb-pro-current-label');
    if (curLabel && config.comparisonCurrentLabel) curLabel.textContent = config.comparisonCurrentLabel;
    const upLabel = byId('abb-pro-upgrade-label');
    if (upLabel && config.comparisonUpgradeLabel) upLabel.textContent = config.comparisonUpgradeLabel;

    const incItems = lineProps(offer.accLines[0])['Included Items'];
    let includedBundleAlt = 'Included accessories';
    if (config.currentCardTitle && String(config.currentCardTitle).trim() !== '') {
      includedBundleAlt = String(config.currentCardTitle).trim();
    } else if (incItems && String(incItems).trim()) {
      includedBundleAlt = String(incItems).trim();
    }

    const upgradeBundleAlt =
      config.upgradeCardTitle && String(config.upgradeCardTitle).trim() !== ''
        ? String(config.upgradeCardTitle).trim()
        : upgradeVariant.title || upgradeProduct.title || 'Upgrade bundle';

    const curImg = byId('accessory-bundle-upsell-popup-current-image');
    if (curImg) {
      const customSrc = config.currentImageUrl || null;
      if (customSrc) {
        curImg.src = customSrc;
        curImg.alt = includedBundleAlt;
        curImg.style.display = '';
      } else {
        const firstLine = offer.accLines[0];
        const url = firstLine && firstLine.image;
        if (url) {
          curImg.src = url;
          curImg.alt = includedBundleAlt;
          curImg.style.display = '';
        } else {
          curImg.style.display = 'none';
        }
      }
    }

    const upgradeImg = byId('accessory-bundle-upsell-popup-upgrade-image');
    if (upgradeImg && config.showUpgradeImage !== false) {
      let imgSrc = config.upgradeImageUrl || null;
      if (!imgSrc && upgradeVariant.featured_image) {
        imgSrc = upgradeVariant.featured_image.src || (typeof upgradeVariant.featured_image === 'string' ? upgradeVariant.featured_image : null);
      }
      if (!imgSrc && upgradeProduct.featured_image) {
        imgSrc = upgradeProduct.featured_image.src || upgradeProduct.featured_image;
      }
      if (imgSrc) {
        upgradeImg.src = imgSrc;
        upgradeImg.alt = upgradeBundleAlt;
        upgradeImg.style.display = '';
      } else {
        upgradeImg.style.display = 'none';
      }
    }

    overlay.dataset.upgradeBundleVariantId = String(upgradeVariant.id);
    overlay.dataset.abbCartGroupId = offer.groupId;
    overlay.dataset.abbCartLinkedTitle = linkedName;

    const accKeysJson = JSON.stringify(offer.accLines.map(function (l) {
      return l.key;
    }));
    overlay.dataset.abbAccessoryLineKeys = accKeysJson;

    overlay.style.display = 'flex';
    overlay.setAttribute('aria-hidden', 'false');
  }

  function wireCartAccessoryUpgrade() {
    const overlay = byId('accessory-bundle-upsell-popup-overlay');
    const confirmBtn = byId('accessory-bundle-upsell-popup-confirm');
    const continueBtn = byId('accessory-bundle-upsell-popup-continue');
    const closeBtn = byId('accessory-bundle-upsell-popup-close');
    if (!overlay || overlay.getAttribute('data-abb-context') !== 'cart') return;

    function hide() {
      overlay.style.display = 'none';
      overlay.setAttribute('aria-hidden', 'true');
    }

    async function onDecline() {
      const configEl = byId('accessory-bundle-upsell-popup-config');
      let remember = true;
      try {
        const c = configEl && JSON.parse(configEl.textContent);
        if (c && c.rememberChoice === false) remember = false;
      } catch (e) {}
      const gid = overlay.dataset.abbCartGroupId;
      if (remember && gid) writeDecline(gid);
      hide();
    }

    async function onConfirm() {
      const variantId = overlay.dataset.upgradeBundleVariantId;
      const keysRaw = overlay.dataset.abbAccessoryLineKeys;
      const linked = overlay.dataset.abbCartLinkedTitle || 'Mattress';
      const groupId = overlay.dataset.abbCartGroupId;
      if (!variantId || !keysRaw || !groupId) {
        hide();
        return;
      }
      let keys;
      try {
        keys = JSON.parse(keysRaw);
      } catch (e) {
        hide();
        return;
      }
      confirmBtn && (confirmBtn.disabled = true);
      continueBtn && (continueBtn.disabled = true);
      try {
        for (let i = 0; i < keys.length; i++) {
          await changeLineQuantity(keys[i], 0);
        }
        await addUpgradeLine(variantId, {
          'Linked to': linked,
          _bundle_group_id: groupId,
          Bundle: 'Upgraded accessory bundle',
        });
        window.location.href = '/cart?abb_upgraded=1';
      } catch (err) {
        alert(err && err.message ? err.message : 'Could not apply upgrade.');
      } finally {
        confirmBtn && (confirmBtn.disabled = false);
        continueBtn && (continueBtn.disabled = false);
        hide();
      }
    }

    if (confirmBtn) confirmBtn.addEventListener('click', onConfirm);
    if (continueBtn) continueBtn.addEventListener('click', onDecline);
    if (closeBtn) closeBtn.addEventListener('click', onDecline);
  }

  function boot() {
    wireCartAccessoryUpgrade();
    runCartAccessoryUpgrade().catch(function () {});
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
